const DASHBOARD_SIGNALS_STORAGE_KEY = 'vuetify@dashboard-event-signals'
const USER_STORAGE_KEY = 'vuetify@user'
const CLOSED_PENDING_MAX_AGE_MS = 24 * 60 * 60 * 1000
const RECENTLY_ACK_MAX_AGE_MS = 60 * 1000
const DASHBOARD_PERSIST_DEBOUNCE_MS = 250

let dashboardPersistTimer = null

function safeParseJson(value, fallback = {}) {
  try {
    return JSON.parse(value)
  } catch {
    return fallback
  }
}

function safeReadStorage(storageKey, fallback = {}) {
  try {
    return safeParseJson(localStorage.getItem(storageKey), fallback)
  } catch {
    return fallback
  }
}

function safeWriteStorage(storageKey, payload) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(payload))
  } catch (error) {
    console.warn(`Unable to persist ${storageKey}:`, error)
  }
}

function sanitizeDashboardSignals(raw, now = Date.now()) {
  const payload = raw && typeof raw === 'object' ? raw : {}
  const closedPendingRaw = payload.closedPendingAckByEventId || {}
  const recentlyAckRaw = payload.recentlyAcknowledgedClosedByEventId || {}

  const closedPendingAckByEventId = {}
  for (const [eventId, entry] of Object.entries(closedPendingRaw)) {
    if (!entry || typeof entry !== 'object') {
      continue
    }

    const closedDetectedAt = Number(entry.closedDetectedAt || 0)
    if (!closedDetectedAt || now - closedDetectedAt > CLOSED_PENDING_MAX_AGE_MS) {
      continue
    }

    closedPendingAckByEventId[eventId] = {
      segment: entry.segment || null,
      closedDetectedAt,
      lastModifiedAt: entry.lastModifiedAt || null,
    }
  }

  const recentlyAcknowledgedClosedByEventId = {}
  for (const [eventId, entry] of Object.entries(recentlyAckRaw)) {
    if (!entry || typeof entry !== 'object') {
      continue
    }

    const ackedAt = Number(entry.ackedAt || 0)
    if (!ackedAt || now - ackedAt > RECENTLY_ACK_MAX_AGE_MS) {
      continue
    }

    recentlyAcknowledgedClosedByEventId[eventId] = {
      segment: entry.segment || null,
      closedDetectedAt: Number(entry.closedDetectedAt || 0) || null,
      lastModifiedAt: entry.lastModifiedAt || null,
      ackedAt,
    }
  }

  return {
    closedPendingAckByEventId,
    recentlyAcknowledgedClosedByEventId,
  }
}

function persistDashboardSignals(dashboardState) {
  const closedPendingAckByEventId = {}
  for (const [eventId, entry] of Object.entries(dashboardState?.closedPendingAckByEventId || {})) {
    const closedDetectedAt = Number(entry?.closedDetectedAt || 0)
    if (!closedDetectedAt) {
      continue
    }

    closedPendingAckByEventId[eventId] = {
      segment: entry.segment || null,
      closedDetectedAt,
      lastModifiedAt: entry.lastModifiedAt || null,
    }
  }

  const recentlyAcknowledgedClosedByEventId = {}
  for (const [eventId, entry] of Object.entries(
    dashboardState?.recentlyAcknowledgedClosedByEventId || {}
  )) {
    const ackedAt = Number(entry?.ackedAt || 0)
    if (!ackedAt) {
      continue
    }

    recentlyAcknowledgedClosedByEventId[eventId] = {
      segment: entry.segment || null,
      closedDetectedAt: Number(entry.closedDetectedAt || 0) || null,
      lastModifiedAt: entry.lastModifiedAt || null,
      ackedAt,
    }
  }

  const payload = {
    closedPendingAckByEventId,
    recentlyAcknowledgedClosedByEventId,
    persistedAt: Date.now(),
  }

  safeWriteStorage(DASHBOARD_SIGNALS_STORAGE_KEY, payload)
}

function persistUserField(field, value) {
  const currentStorage = safeReadStorage(USER_STORAGE_KEY, {})
  currentStorage[field] = value
  safeWriteStorage(USER_STORAGE_KEY, currentStorage)
}

function scheduleDashboardSignalsPersist(dashboardState) {
  if (dashboardPersistTimer) {
    clearTimeout(dashboardPersistTimer)
  }

  dashboardPersistTimer = setTimeout(() => {
    persistDashboardSignals(dashboardState)
    dashboardPersistTimer = null
  }, DASHBOARD_PERSIST_DEBOUNCE_MS)
}

export function hydrateDashboardSignals(store) {
  const raw = safeReadStorage(DASHBOARD_SIGNALS_STORAGE_KEY, {})
  const signals = sanitizeDashboardSignals(raw)

  store.commit('dashboard/closedPendingAckByEventId', signals.closedPendingAckByEventId)
  store.commit(
    'dashboard/recentlyAcknowledgedClosedByEventId',
    signals.recentlyAcknowledgedClosedByEventId
  )
}

export function attachStorePersistence(store) {
  store.subscribe(mutation => {
    if (mutation.type.startsWith('user/')) {
      const field = mutation.type.replace('user/', '')

      const skipFields = ['fetched', 'profile', 'mainTabs', 'email', 'first_name', 'last_name']
      if (skipFields.includes(field)) {
        return
      }

      persistUserField(field, mutation.payload)
      return
    }

    if (mutation.type.startsWith('dashboard/')) {
      const field = mutation.type.replace('dashboard/', '')
      if (
        field === 'closedPendingAckByEventId' ||
        field === 'recentlyAcknowledgedClosedByEventId'
      ) {
        scheduleDashboardSignalsPersist(store.state.dashboard)
      }
    }
  })
}
